// src/data/services.js
// Default content + icon registry for Follex Tech's services, partners,
// audiences and trust badges.
//
// IMPORTANT: this content is now ADMIN-EDITABLE. The live values come from
// `settings/siteContent` via useSiteContent(); the arrays below are only the
// fallback defaults. Because Firestore cannot store React components, every
// item references an icon by STRING KEY (`iconKey`) which is resolved to an
// SVG icon component at render time via getIcon(). Per UI guidance we use SVG
// icons (react-icons), never emoji, for structural icons.

import {
  FaDesktop, FaNetworkWired, FaShieldAlt, FaCode, FaBolt,
  FaServer, FaTools, FaWifi, FaVideo, FaLaptopCode, FaDatabase,
  FaAward, FaUsers, FaHeadset, FaCheckCircle, FaClock, FaThumbsUp,
  FaBuilding, FaGraduationCap, FaLandmark, FaHospital, FaStore, FaHome,
} from 'react-icons/fa';

// ── Icon registry ──────────────────────────────────────────────────────────
// Single place that maps a stable string key → an SVG icon component.
export const ICONS = {
  desktop: FaDesktop,
  network: FaNetworkWired,
  shield: FaShieldAlt,
  code: FaCode,
  bolt: FaBolt,
  server: FaServer,
  tools: FaTools,
  wifi: FaWifi,
  video: FaVideo,
  laptopCode: FaLaptopCode,
  database: FaDatabase,
  award: FaAward,
  users: FaUsers,
  headset: FaHeadset,
  check: FaCheckCircle,
  clock: FaClock,
  thumbsUp: FaThumbsUp,
  building: FaBuilding,
  school: FaGraduationCap,
  government: FaLandmark,
  hospital: FaHospital,
  store: FaStore,
  home: FaHome,
};

// Resolve an icon key to a component, with a safe fallback.
export const getIcon = (key) => ICONS[key] || FaCheckCircle;

// Grouped key lists for admin dropdowns.
export const SERVICE_ICON_KEYS = [
  'desktop', 'network', 'shield', 'code', 'bolt',
  'server', 'tools', 'wifi', 'video', 'laptopCode', 'database',
];
export const AUDIENCE_ICON_KEYS = [
  'building', 'school', 'government', 'hospital', 'store', 'home', 'users',
];
export const BADGE_ICON_KEYS = [
  'award', 'users', 'headset', 'shield', 'bolt', 'check', 'clock', 'thumbsUp',
];

// ── Default services ─────────────────────────────────────────────────────────
export const defaultServices = [
  {
    slug: 'ict-services',
    title: 'ICT Services',
    iconKey: 'desktop',
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=900&q=60&auto=format&fit=crop',
    tagline: 'Keep your technology running, every day.',
    summary:
      'End-to-end IT support, repairs and infrastructure management for businesses, institutions and individuals.',
    offerings: [
      'Computer Repair & Maintenance',
      'IT Support & Helpdesk',
      'Server Installation & Management',
      'System Administration',
      'Data Backup & Recovery',
      'IT Consultancy',
    ],
  },
  {
    slug: 'networking-solutions',
    title: 'Networking Solutions',
    iconKey: 'network',
    image: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=900&q=60&auto=format&fit=crop',
    tagline: 'Fast, secure and reliable connectivity.',
    summary:
      'We design, install and manage wired and wireless networks that scale with your organisation.',
    offerings: [
      'Network Design & Implementation',
      'Structured Cabling',
      'LAN & WAN Installation',
      'MikroTik Configuration',
      'Wireless Solutions',
      'VPN Setup & Support',
    ],
  },
  {
    slug: 'security-systems',
    title: 'Security Systems',
    iconKey: 'shield',
    image: 'https://images.unsplash.com/photo-1558002038-1055907df827?w=900&q=60&auto=format&fit=crop',
    tagline: 'Protect what matters most.',
    summary:
      'Modern surveillance and access control to keep your people, premises and assets safe.',
    offerings: [
      'CCTV Installation & Maintenance',
      'Access Control Systems',
      'Biometric Systems',
      'Alarm Systems',
      'Surveillance Solutions',
    ],
  },
  {
    slug: 'software-web-development',
    title: 'Software & Web Development',
    iconKey: 'code',
    image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=900&q=60&auto=format&fit=crop',
    tagline: 'Custom software that drives your business.',
    summary:
      'From websites and e-commerce to bespoke business systems, hosting and databases.',
    offerings: [
      'Custom Software Development',
      'Website Design & Development',
      'E-commerce Solutions',
      'Web Hosting & Domains',
      'Database Design & Management',
    ],
  },
  {
    slug: 'electrical-services',
    title: 'Electrical Services',
    iconKey: 'bolt',
    image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=900&q=60&auto=format&fit=crop',
    tagline: 'Safe connections. Stronger future.',
    summary:
      'Registration, installation and maintenance for residential, commercial and industrial electrical works.',
    offerings: [
      'Electrical System Design',
      'Electrical Installation',
      'Wiring & Rewiring',
      'Distribution Board Installation',
      'Lighting Installation',
      'Industrial Electrical Works',
      'Commercial Installations',
      'Residential Installations',
      'Electrical Fault Diagnosis',
      'Preventive Maintenance',
      'Electrical Compliance Support',
    ],
  },
];

// Find a service by slug within a (possibly admin-edited) list.
export const findService = (list, slug) =>
  (list || []).find((s) => s.slug === slug);

// Defensive slug helper for admin-created services.
export const slugify = (str) =>
  (str || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

// ── "Authorised Dealer & Partner" — styled wordmarks (no logo assets) ────────
export const defaultPartners = [
  'HP', 'Dell', 'Lenovo', 'Toshiba', 'Acer', 'Apple', 'Compaq',
];

// ── "We proudly serve…" ──────────────────────────────────────────────────────
export const defaultAudiences = [
  { label: 'Corporate Organizations', iconKey: 'building' },
  { label: 'Schools', iconKey: 'school' },
  { label: 'Government Institutions', iconKey: 'government' },
  { label: 'Hospitals', iconKey: 'hospital' },
  { label: 'SMEs', iconKey: 'store' },
  { label: 'Residential Clients', iconKey: 'home' },
];

// ── Trust badges ─────────────────────────────────────────────────────────────
export const defaultTrustBadges = [
  { title: 'Quality Service', desc: 'Workmanship you can rely on', iconKey: 'award' },
  { title: 'Experienced Team', desc: 'Skilled, certified technicians', iconKey: 'users' },
  { title: 'Fast Response', desc: 'Quick turnaround, minimal downtime', iconKey: 'headset' },
  { title: 'Reliable & Trusted', desc: 'Trusted across Kenya', iconKey: 'shield' },
];

// How we work — shared default process for service detail pages.
export const defaultProcess = [
  { step: '01', title: 'Consultation', desc: 'We assess your needs and site, then recommend the right solution.' },
  { step: '02', title: 'Implementation', desc: 'Our certified team installs and configures everything to standard.' },
  { step: '03', title: 'Support', desc: 'Ongoing maintenance, monitoring and fast after-sales support.' },
];
